// Procedurally drawn mobster portraits. Each character has a numeric "portrait"
// seed; the same seed always draws the same face. A supplied image at
// assets/portraits/player-<id>.png (listed in the manifest) replaces it.
import { useId } from 'react';
import { assetUrl } from './assets.ts';

function rng(seed: number) {
  let a = seed >>> 0 || 1;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const pick = <T,>(r: () => number, a: T[]) => a[Math.floor(r() * a.length)];

const SKIN = ['#f1c9a5', '#e0ac85', '#c68d63', '#a86f48', '#8a5534', '#5e3a22', '#f5d6bd'];
const HAIR = ['#111', '#2a1a10', '#4a2e1a', '#6a4a2a', '#888', '#bbb', '#a0522d', '#d8b070'];
const SUIT = ['#1a1a1a', '#222833', '#2e2620', '#3a3a3a', '#1c2a1c', '#2a1818', '#f0f0f0'];
const TIE = ['#a01010', '#d4a017', '#1a3a8a', '#111', '#5a1a5a', '#2a6a2a', '#eee'];
const BG = [['#3a2a1a', '#120b05'], ['#1a2a3a', '#050b12'], ['#3a1a1a', '#120505'], ['#2a2a2a', '#0a0a0a'], ['#2a3a2a', '#050c05']];

export function Portrait({ seed, size = 50, playerId }: { seed: number; size?: number; playerId?: number }) {
  const id = useId().replace(/:/g, '');
  const supplied = playerId !== undefined ? assetUrl(`portraits/player-${playerId}`) : null;
  if (supplied) return <img className="portrait" src={supplied} width={size} height={size} alt="" />;

  const r = rng(seed);
  const female = r() < 0.2;
  const skin = pick(r, SKIN);
  const hair = pick(r, HAIR);
  const suit = pick(r, SUIT);
  const tie = pick(r, TIE);
  const bg = pick(r, BG);
  const hat = !female && r() < 0.55;
  const hatColor = pick(r, ['#111', '#2a2a2a', '#4a3a2a', '#ddd', '#3a3a4a']);
  const shades = r() < 0.18;
  const mustache = !female && r() < 0.35;
  const beard = !female && !mustache && r() < 0.15;
  const cigar = !female && r() < 0.15;
  const scar = r() < 0.1;
  const faceW = 15 + r() * 3;
  const jaw = 20 + r() * 4;
  const brow = r() * 2;

  return (
    <svg className="portrait" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <radialGradient id={`${id}b`} cx="0.5" cy="0.35" r="0.8">
          <stop offset="0" stopColor={bg[0]} />
          <stop offset="1" stopColor={bg[1]} />
        </radialGradient>
        <linearGradient id={`${id}s`} x1="0" x2="1">
          <stop offset="0" stopColor="#000" stopOpacity="0.35" />
          <stop offset="0.45" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.45" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" fill={`url(#${id}b)`} />
      {female && <path d={`M${50 - faceW - 7} 40 q0 -22 ${faceW + 7} -24 q${faceW + 7} 2 ${faceW + 7} 24 v38 h-${2 * faceW + 14}z`} fill={hair} />}
      {/* shoulders & suit */}
      <path d="M8 100 q4 -26 30 -30 l12 6 l12 -6 q26 4 30 30z" fill={suit} />
      <path d="M38 70 l12 22 l12 -22 l-12 6z" fill={female ? skin : '#eee'} />
      {!female && <path d="M47 76 l3 -2 l3 2 l-1.5 18 h-3z" fill={tie} />}
      <path d="M38 70 l10 26 l-14 -18z M62 70 l-10 26 l14 -18z" fill="#000" opacity="0.35" />
      {/* neck & head */}
      <rect x="43" y="56" width="14" height="16" fill={skin} />
      <path d={`M${50 - faceW} 40 q0 -22 ${faceW} -22 q${faceW} 0 ${faceW} 22 q0 ${jaw - 4} -${faceW} ${jaw} q-${faceW} -4 -${faceW} -${jaw}z`} fill={skin} />
      <path d={`M${50 - faceW} 40 q0 -22 ${faceW} -22 q${faceW} 0 ${faceW} 22 q0 ${jaw - 4} -${faceW} ${jaw} q-${faceW} -4 -${faceW} -${jaw}z`} fill={`url(#${id}s)`} />
      {/* ears */}
      <ellipse cx={50 - faceW} cy="42" rx="3" ry="5" fill={skin} />
      <ellipse cx={50 + faceW} cy="42" rx="3" ry="5" fill={skin} />
      {/* hair */}
      {!hat && !female && <path d={`M${50 - faceW - 1} 36 q0 -20 ${faceW + 1} -19 q${faceW + 1} -1 ${faceW + 1} 19 q-4 -10 -${faceW} -10 q-${faceW - 4} 0 -${faceW + 1} 10z`} fill={hair} />}
      {female && <path d={`M${50 - faceW} 34 q4 -16 ${faceW} -16 q${faceW - 4} 0 ${faceW} 16 q-8 -8 -${faceW} -8z`} fill={hair} />}
      {/* eyes */}
      {shades ? (
        <g>
          <rect x={50 - faceW + 3} y="36" width={faceW - 4} height="7" rx="2" fill="#050505" />
          <rect x="51" y="36" width={faceW - 4} height="7" rx="2" fill="#050505" />
          <path d={`M${50 - 1} 38 h2`} stroke="#050505" strokeWidth="1.5" />
        </g>
      ) : (
        <g>
          <ellipse cx={50 - faceW / 2} cy="40" rx="2.4" ry="1.8" fill="#1a1a1a" />
          <ellipse cx={50 + faceW / 2} cy="40" rx="2.4" ry="1.8" fill="#1a1a1a" />
          <path d={`M${50 - faceW / 2 - 4} ${35 + brow} l8 ${-brow + 0.5} M${50 + faceW / 2 - 4} ${35.5} l8 ${brow}`} stroke={female ? '#3a2a1a' : hair === '#bbb' ? '#888' : '#1a1008'} strokeWidth="2" />
        </g>
      )}
      {/* nose & mouth */}
      <path d="M50 41 l-2 9 h4" fill="none" stroke="#000" strokeOpacity="0.35" strokeWidth="1.2" />
      <path d="M45 55 q5 2 10 0" fill="none" stroke={female ? '#a03030' : '#5a2a1a'} strokeWidth={female ? 2 : 1.4} />
      {mustache && <path d="M43 52.5 q7 -4 14 0 q-7 1.5 -14 0z" fill={hair} />}
      {beard && <path d={`M${50 - faceW + 3} 48 q${faceW - 3} 18 ${faceW - 3} 18 q${faceW - 3} 0 ${faceW - 3} -18 q-3 8 -${faceW - 3} 9 q-${faceW - 6} -1 -${faceW - 3} -9z`} fill={hair} opacity="0.85" />}
      {cigar && (
        <g>
          <rect x="54" y="54" width="14" height="3.4" rx="1.5" fill="#6a3a1a" transform="rotate(12 54 55)" />
          <circle cx="68" cy="58" r="1.6" fill="#f60" />
          <path d="M69 55 q3 -6 0 -12 q-2 -4 1 -8" fill="none" stroke="#aaa" strokeOpacity="0.5" />
        </g>
      )}
      {scar && <path d={`M${50 + faceW / 2 - 2} 44 l4 8`} stroke="#8a3a2a" strokeWidth="1.2" />}
      {/* fedora */}
      {hat && (
        <g>
          <ellipse cx="50" cy="29" rx={faceW + 13} ry="5" fill={hatColor} />
          <path d={`M${50 - faceW + 1} 29 q0 -16 ${faceW - 1} -15 q${faceW - 1} -1 ${faceW - 1} 15z`} fill={hatColor} />
          <path d={`M${50 - faceW + 1} 26 h${2 * faceW - 2} v3 h-${2 * faceW - 2}z`} fill="#000" opacity="0.6" />
          <path d="M44 16 q6 4 12 0" fill="none" stroke="#000" strokeOpacity="0.4" />
        </g>
      )}
      <rect width="100" height="100" fill="#704214" opacity="0.08" />
    </svg>
  );
}
